import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { POSITION } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { NoBubblesEventInit } from "@cervval.js/common/src/tools/event.tools";
import { isInside, toPx } from "@cervval.js/common/src/tools/number.tools";
import { MILLIS_IN_ONE_MINUTE, MILLIS_IN_ONE_SECOND } from "@cervval.js/common/src/tools/time.tools";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { getTodayDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { timestampToPxY } from "@cervval.js/common/src/tools/timestamps.tools.ui";
import { CjsDayAgenda } from "../dayAgenda/dayAgenda";
import { } from "../dayAgenda/dayAgenda.types";


export type DayAgendaCursor = HTMLDivElement;

// Class added when cursor is dragged
export const CURSOR_DRAGGED = "cursor-dragged";

const DEFAULT_CURSOR_Z_INDEX = 10;

export interface DayAgendaCursorOptions {

    /** Width of cursor (in px) */
    height: number;

    /** Color of cursor (accepts rgb & hexadecimal) */
    color: string;
}

export const defaultDayAgendaCursorOptions: DayAgendaCursorOptions = {

    /** Height of cursor (in px) */
    height: 2,

    /** Color of cursor (accepts rgb & hexadecimal) */
    color: "red"
};

@CustomElement({
    name: "cjs-day-agenda-cursor",
    style: require("./dayAgendaCursor.scss"),
    template: require("./dayAgendaCursor.html"),
    elementCssClasses: [POSITION.ABSOLUTE],
    doNotRefreshAfterInit: true// Refresh is handled by agenda
})
export class CjsDayAgendaCursor<ItemObject, ColumnObject> extends WebComponent {

    @TypedHtmlElements()
    public readonly components!: {
        topPointer: HTMLDivElement,
        bottomPointer: HTMLDivElement,
        cursorContainer: HTMLDivElement,
        cursor: DayAgendaCursor
    };

    @Event(NoBubblesEventInit)
    public cursorMovedEvent!: EventHandler<{ fromRightClick?: boolean }>;

    @TypedWebComponentOptions(defaultDayAgendaCursorOptions)
    public readonly options!: WebComponentOptions<DayAgendaCursorOptions>;

    /** True if cursor is installed */
    public installed = false;

    /** Agenda is installed with "install" call */
    private _agenda!: CjsDayAgenda<ItemObject, ColumnObject>;

    /** Cursor to drag */
    public get cursor(): DayAgendaCursor { return this.components.cursor; }

    /** Agenda cols container */
    public get columnsContainer(): HTMLDivElement { return this._agenda.components.columnsContainer; }

    constructor(options: Partial<DayAgendaCursorOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.style.zIndex = String(DEFAULT_CURSOR_Z_INDEX);
    }

    /** Install cursor for agenda */
    public install(agenda: CjsDayAgenda<ItemObject, ColumnObject>): void {
        this.installed = true;
        this._agenda = agenda;

        // Subscribe to colsViews refresh
        this._agenda.endOfRefreshViewEvent.on(() => this.refresh());

        // Create an interval who check when minute has started every second
        this._checkMinuteIntervalId = setInterval(() => this.checkMinutes(), MILLIS_IN_ONE_SECOND);
    }

    /** Refresh of component */
    public override refresh(): void {
        this._refreshPosition();
        this._refreshColorStyle();
        this._refreshWidthStyle();
    }

    /** Minute interval Id */
    private _checkMinuteIntervalId: number | undefined;

    /** Check current minute interval */
    public checkMinutes(): void {
        const now = new Date().getSeconds();

        if (now === 0) {

            // Refresh now
            this.refresh();
            // Then refresh every minute
            setInterval(() => this.refresh(), MILLIS_IN_ONE_MINUTE);

            // Clear check interval
            if (this._checkMinuteIntervalId) {
                clearInterval(this._checkMinuteIntervalId);
            }
        }
    }

    /** Refresh color style */
    private _refreshColorStyle(): void {
        this.style.setProperty("--agenda-cursor-color", this.options.color);
    }

    /** Refresh width style */
    private _refreshWidthStyle(): void {
        this.style.setProperty("--agenda-cursor-width", toPx(this.options.height));
    }

    /** Refresh cursor position */
    private _refreshPosition(): void {

        const value = NOW();
        const today = getTodayDate();
        toggleVisible(this, this.isInAgendaBounds(), Display.BLOCK);

        const currentColViews = this._agenda.columnViewsByDayCache[today.uts];

        // Impossible to setUp unique cursor position if multiple colView on today // TODO improve with multiple cursors ?
        if (!currentColViews || currentColViews.length !== 1) {
            setDisplay(this, Display.NONE);
            return;
        }

        const colView = currentColViews[0];
        const colViewContext = colView.columnContext;
        if (!colViewContext) {
            setDisplay(this, Display.NONE);
            return;
        }

        // Compute new top value of cursor
        const newTop = timestampToPxY(colViewContext, { ts: value });
        this.style.top = toPx(newTop);
        this.style.left = toPx(colView.offsetLeft);
        this.style.width = toPx(colView.clientWidth);

        this.columnsContainer.append(this);
    }


    /** @returns true is value of cursor is in agenda displayed range */
    public isInAgendaBounds(): boolean {
        const context = this._agenda.getAgendaContext();
        return isInside(NOW(), context.startTs, context.finishTs);
    }
}
